<template>
  <div class="image-uploader">
    <v-file-input
      v-model="selectedFile"
      accept="image/*"
      :label="displayLabel"
      prepend-icon="mdi-camera"
      hide-details="auto"
      dense
      clearable
      @change="handleFileSelect"
      @click:clear="handleRemove"
    />
    <div v-if="previewUrl || serverImagePath" class="image-preview">
      <v-img
        position="left"
        :src="previewUrl || serverImagePath"
        :max-height="imageSize"
        :max-width="imageSize"
        contain
        rounded
      />

      <v-btn
        v-if="serverImagePath && !selectedFile"
        icon
        small
        color="text"
        class="remove-btn"
        @click="handleRemove"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
/**
 * 이전에 표시된 이미지 표시 및 새로운 이미지 선택 및 미리보기
 * @props serverImagePath : 실제 이미지 경로
 * @data selectedFile : 선택한 이미지 파일
 * @return 선택한 이미지 파일 반환
 *  */
export default {
  name: 'ImageUploader',
  props: {
    // 서버에 저장된 현재 이미지 경로
    serverImagePath: {
      type: String,
      default: null
    },
    // 파일 입력 라벨
    label: {
      type: String,
      default: ''
    },
    // 파일 크기 제한 (MB)
    maxSize: {
      type: Number,
      default: 5
    },
    // 현재 선택된 파일
    file: {
      type: File,
      default: null
    },
    imageSize: {
      type: Number,
      default: 150,
      required: false
    }
  },
  emits: ['error', 'update', 'remove'],
  data: () => ({
    selectedFile: null,
    previewUrl: null
  }),
  computed: {
    displayLabel() {
      return this.label || this.$t('upload.selectImage');
    }
  },
  watch: {
    file: {
      immediate: true,
      handler(newFile) {
        this.selectedFile = newFile;
        if (newFile) {
          this.createPreviewUrl(newFile);
        }
      }
    }
  },
  beforeUnmount() {
    // 컴포넌트 제거 시 URL 해제
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  },
  methods: {
    handleFileSelect(file) {
      if (file) {
        // 파일 크기 체크
        if (file.size > this.maxSize * 1024 * 1024) {
          this.$emit(
            'error',
            this.$t('upload.fileSizeExceeded', { max: this.maxSize })
          );
          this.selectedFile = null;
          return;
        }

        //이미지 미리보기
        this.createPreviewUrl(file);
        //업로드할 파일 전달
        this.$emit('update', file);
      }
    },
    createPreviewUrl(file) {
      // 이전 URL이 있다면 해제
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
      }
      // 새 파일의 URL 생성
      this.previewUrl = URL.createObjectURL(file);
    },
    handleRemove() {
      // URL 해제
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
      }
      this.previewUrl = null;
      this.selectedFile = null;
      this.$emit('update', null);
      this.$emit('remove');
    }
  }
};
</script>

<style scoped>
.image-uploader {
  position: relative;
}

.image-preview {
  position: relative;
  display: inline-block;
}

.remove-btn {
  position: absolute;
  top: 0;
  right: 0;
  background-color: color-mix(in srgb, var(--v-text-base) 50%, transparent);
  color: var(--v-text-base);
}

.remove-btn:hover {
  background-color: color-mix(in srgb, var(--v-text-base) 70%, transparent);
}
</style>
